import { PERMISSIONS } from "@/constants/permissions"
import { useGuardedMutation } from "@/hooks/useGuardedMutation"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query"
import { apiClientV2 } from "../../../services/api/apiClient"
import { isInFlightStatus } from "../components/customer-requests/processing/processingSteps"
import { toastApiError } from "./apiError"
import { QUESTIONNAIRES_BASE } from "./customerRequestsApi"
import type {
  AnswerCellOut,
  AnswerOverrideIn,
  CoverageOut,
  FileDownloadOut,
  GapActionOut,
  GapsOut,
  MappingConfirmIn,
  MatrixOut,
  ProcessingStatusOut,
  ProcessStartedOut,
  QuestionnaireMappingOut,
  ReconciliationOut,
  ReconciliationResolveIn,
  SendResultOut,
  SheetsOut,
} from "./types/questionnaireProcessing"

// Backend errors are usually `{ detail: "…" }` (a plain string), which never
// carries a `code`. `apiErrorMessage` reads that fine, it just always falls
// back to `MC_API_ERROR_GENERIC` for this file's errors (none of them have a
// mapped code of their own).
function onMutationError(
  error: unknown,
  translate: (
    key: TranslationKeys,
    values?: Record<string, string | number>,
  ) => string,
) {
  toastApiError(error, translate, "MC_API_ERROR_GENERIC")
}

const qpKeys = {
  status: (id: string) => ["mc", "qproc", "status", id],
  mapping: (id: string) => ["mc", "qproc", "mapping", id],
  reconciliation: (id: string) => ["mc", "qproc", "reconciliation", id],
  gaps: (id: string) => ["mc", "qproc", "gaps", id],
  matrix: (id: string) => ["mc", "qproc", "matrix", id],
  coverage: (id: string) => ["mc", "qproc", "coverage", id],
  sheets: (id: string) => ["mc", "qproc", "sheets", id],
  file: (id: string) => ["mc", "qproc", "file", id],
}

function seedProcessingStatus(
  queryClient: QueryClient,
  id: string,
  processingStatus: ProcessingStatusOut["processingStatus"],
) {
  queryClient.setQueryData<ProcessingStatusOut>(qpKeys.status(id), (old) =>
    old ? { ...old, processingStatus } : old,
  )
}

export function useProcessingStatus(id: string | null) {
  return useQuery({
    queryKey: qpKeys.status(id ?? ""),
    queryFn: () =>
      apiClientV2
        .get(`${QUESTIONNAIRES_BASE}/${id}/processing-status`)
        .then((r) => r.data as ProcessingStatusOut),
    enabled: !!id,
    // A gate endpoint claims the row and hands off to a background worker —
    // poll this lightweight status endpoint while the worker owns the row.
    refetchInterval: (query) =>
      isInFlightStatus(query.state.data?.processingStatus ?? null)
        ? 2_000
        : false,
  })
}

export function useMapping(id: string | null) {
  return useQuery({
    queryKey: qpKeys.mapping(id ?? ""),
    queryFn: () =>
      apiClientV2
        .get(`${QUESTIONNAIRES_BASE}/${id}/mapping`)
        .then((r) => r.data as QuestionnaireMappingOut),
    enabled: !!id,
  })
}

export function useConfirmMapping() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const invalidate = (id: string) => {
    queryClient.invalidateQueries({ queryKey: qpKeys.mapping(id) })
    queryClient.invalidateQueries({ queryKey: qpKeys.status(id) })
  }
  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: MappingConfirmIn }) =>
      apiClientV2
        .put(`${QUESTIONNAIRES_BASE}/${id}/mapping`, body)
        .then((r) => r.data as QuestionnaireMappingOut),
    onSuccess: (data, { id }) => {
      seedProcessingStatus(queryClient, id, data.processingStatus)
      invalidate(id)
    },
    onError: (error, { id }) => {
      onMutationError(error, translate)
      invalidate(id)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useReconciliation(id: string | null) {
  return useQuery({
    queryKey: qpKeys.reconciliation(id ?? ""),
    queryFn: () =>
      apiClientV2
        .get(`${QUESTIONNAIRES_BASE}/${id}/reconciliation`)
        .then((r) => r.data as ReconciliationOut),
    enabled: !!id,
  })
}

export function useResolveReconciliation() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const invalidate = (id: string) => {
    queryClient.invalidateQueries({ queryKey: qpKeys.reconciliation(id) })
    queryClient.invalidateQueries({ queryKey: qpKeys.status(id) })
  }
  const mutation = useMutation({
    mutationFn: ({ id, body }: { id: string; body: ReconciliationResolveIn }) =>
      apiClientV2
        .post(`${QUESTIONNAIRES_BASE}/${id}/reconciliation/resolve`, body)
        .then((r) => r.data as ReconciliationOut),
    onSuccess: (data, { id }) => {
      seedProcessingStatus(queryClient, id, data.processingStatus)
      invalidate(id)
    },
    onError: (error, { id }) => {
      onMutationError(error, translate)
      invalidate(id)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_RECONCILE)
}

/** The gaps list, as a plain read. Chat resolves an answer by its part number
 *  and question rather than by an id, so it needs the same list the screen shows. */
export async function fetchGaps(id: string): Promise<GapsOut> {
  const { data } = await apiClientV2.get(`${QUESTIONNAIRES_BASE}/${id}/gaps`)
  return data as GapsOut
}

/** "Override · data is fine" — accept an aged answer as current. */
export async function acceptStaleRequest(
  id: string,
  answerId: string,
): Promise<AnswerCellOut> {
  const { data } = await apiClientV2.post(
    `${QUESTIONNAIRES_BASE}/${id}/gaps/${answerId}/accept-stale`,
  )
  return data as AnswerCellOut
}

/** Type a value into one answer cell. Response-scoped — master data untouched. */
export async function overrideAnswerRequest(
  id: string,
  answerId: string,
  value: string,
): Promise<AnswerCellOut> {
  const { data } = await apiClientV2.patch(
    `${QUESTIONNAIRES_BASE}/${id}/answers/${answerId}`,
    { value } satisfies AnswerOverrideIn,
  )
  return data as AnswerCellOut
}

/** Every cache an answer write moves. Shared with the two hooks below, because a
 *  chat write against an open wizard has to refresh what the reviewer is looking
 *  at — react-query serves what it has, and stale rows read as a failed write. */
export function invalidateAnswerWrites(queryClient: QueryClient, id: string) {
  queryClient.invalidateQueries({ queryKey: qpKeys.gaps(id) })
  queryClient.invalidateQueries({ queryKey: qpKeys.matrix(id) })
  queryClient.invalidateQueries({ queryKey: qpKeys.sheets(id) })
}

export function useGaps(id: string | null) {
  return useQuery({
    queryKey: qpKeys.gaps(id ?? ""),
    queryFn: () =>
      apiClientV2
        .get(`${QUESTIONNAIRES_BASE}/${id}/gaps`)
        .then((r) => r.data as GapsOut),
    enabled: !!id,
  })
}

export function useAcceptStale() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const invalidate = (id: string) => invalidateAnswerWrites(queryClient, id)
  const mutation = useMutation({
    mutationFn: ({ id, answerId }: { id: string; answerId: string }) =>
      acceptStaleRequest(id, answerId),
    onSuccess: (_data, { id }) => invalidate(id),
    onError: (error, { id }) => {
      onMutationError(error, translate)
      invalidate(id)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useAnswerOverride() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const invalidate = (id: string) => invalidateAnswerWrites(queryClient, id)
  const mutation = useMutation({
    mutationFn: ({
      id,
      answerId,
      body,
    }: {
      id: string
      answerId: string
      body: AnswerOverrideIn
    }) => overrideAnswerRequest(id, answerId, body.value),
    onSuccess: (_data, { id }) => invalidate(id),
    onError: (error, { id }) => {
      onMutationError(error, translate)
      invalidate(id)
    },
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useGapRefresh() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({ id, itemId }: { id: string; itemId: string }) =>
      apiClientV2
        .post(`${QUESTIONNAIRES_BASE}/${id}/gaps/${itemId}/refresh`)
        .then((r) => r.data as GapActionOut),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: qpKeys.gaps(id) })
    },
    onError: (error) => onMutationError(error, translate),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useGapToMasterData() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: ({ id, itemId }: { id: string; itemId: string }) =>
      apiClientV2
        .post(`${QUESTIONNAIRES_BASE}/${id}/gaps/${itemId}/to-master-data`)
        .then((r) => r.data as GapActionOut),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: qpKeys.gaps(id) })
    },
    onError: (error) => onMutationError(error, translate),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useMatrix(id: string | null) {
  return useQuery({
    queryKey: qpKeys.matrix(id ?? ""),
    queryFn: () =>
      apiClientV2
        .get(`${QUESTIONNAIRES_BASE}/${id}/matrix`)
        .then((r) => r.data as MatrixOut),
    enabled: !!id,
  })
}

export function useCoverage(id: string | null) {
  return useQuery({
    queryKey: qpKeys.coverage(id ?? ""),
    queryFn: () =>
      apiClientV2
        .get(`${QUESTIONNAIRES_BASE}/${id}/coverage`)
        .then((r) => r.data as CoverageOut),
    enabled: !!id,
  })
}

export function useStartRender() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClientV2
        .post(`${QUESTIONNAIRES_BASE}/${id}/render`)
        .then((r) => r.data as ProcessStartedOut),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: qpKeys.status(id) })
    },
    onError: (error) => onMutationError(error, translate),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useResponseFile(
  id: string | null,
  opts?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: qpKeys.file(id ?? ""),
    queryFn: () =>
      apiClientV2
        .get(`${QUESTIONNAIRES_BASE}/${id}/response/file`)
        .then((r) => r.data as FileDownloadOut),
    enabled: !!id && (opts?.enabled ?? true),
    // 404 until a render has produced a file — don't retry into a busy-loop.
    retry: false,
  })
}

export function useSendToCustomer() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClientV2
        .post(`${QUESTIONNAIRES_BASE}/${id}/send`)
        .then((r) => r.data as SendResultOut),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: qpKeys.status(id) })
    },
    onError: (error) => onMutationError(error, translate),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useReopen() {
  const queryClient = useQueryClient()
  const { translate } = useTranslation()
  const mutation = useMutation({
    mutationFn: (id: string) =>
      apiClientV2
        .post(`${QUESTIONNAIRES_BASE}/${id}/reopen`)
        .then((r) => r.data as MatrixOut),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: qpKeys.status(id) })
      queryClient.invalidateQueries({ queryKey: qpKeys.matrix(id) })
      queryClient.invalidateQueries({ queryKey: qpKeys.sheets(id) })
    },
    onError: (error) => onMutationError(error, translate),
  })
  return useGuardedMutation(mutation, PERMISSIONS.MC_QUESTIONNAIRES_PROCESS)
}

export function useSheets(id: string | null) {
  return useQuery({
    queryKey: qpKeys.sheets(id ?? ""),
    queryFn: () =>
      apiClientV2
        .get(`${QUESTIONNAIRES_BASE}/${id}/sheets`)
        .then((r) => r.data as SheetsOut),
    enabled: !!id,
  })
}
